<template>
    <div
        class="card"
        data-cy="stock-compartido-panel"
    >
        <div class="card-header">
            <h3 class="card-title mb-0">
                <i class="fas fa-ticket-alt tw:mr-2" aria-hidden="true"></i>
                Stock compartido
            </h3>
            <p class="card-text tw:mt-2 tw:mb-0 tw:text-sm tw:text-gray-600">
                Un único cupo total se comparte entre todos los precios y entradas de esta sesión.
            </p>
        </div>

        <div class="card-body">
            <!-- Skeleton while initial load is in flight -->
            <Skeleton
                v-if="loading"
                aria-busy="true"
                aria-live="polite"
                data-cy="stock-compartido-skeleton"
            >
                <template #skeleton>
                    <SkeletonItem height="tw:h-6" custom-class="tw:w-48" />
                    <SkeletonItem height="tw:h-10" custom-class="tw:w-full tw:mt-3" />
                    <SkeletonItem height="tw:h-10" custom-class="tw:w-1/2 tw:mt-2" />
                    <SkeletonItem height="tw:h-10" custom-class="tw:w-32 tw:mt-4" />
                </template>
            </Skeleton>

            <!-- Empty state when stock configuration is unavailable -->
            <div
                v-else-if="dataUnavailable"
                class="tw:text-center tw:py-10 tw:text-gray-500"
                data-cy="stock-compartido-unavailable"
            >
                <i class="fas fa-ticket-alt tw:text-4xl tw:mb-3 tw:block tw:opacity-40"></i>
                <p class="tw:text-lg">
                    La configuración de stock compartido no está disponible para esta sesión.
                </p>
            </div>

            <!-- Venue-backed 422: server rejected activation. The form remains
                 in the markup but every control is disabled (REQ-SC-07). -->
            <template v-else>
                <div
                    v-if="venueBackedDisabled"
                    class="alert alert-danger tw:rounded tw:border tw:border-red-300 tw:bg-red-50 tw:p-4"
                    role="alert"
                    aria-live="assertive"
                    data-cy="stock-compartido-venue-banner"
                >
                    <p class="tw:font-semibold">
                        Este evento tiene recinto. Stock compartido no aplica.
                    </p>
                </div>

                <div
                    v-if="permissionDenied"
                    class="alert alert-danger tw:rounded tw:border tw:border-red-300 tw:bg-red-50 tw:p-4"
                    role="alert"
                    aria-live="assertive"
                    data-cy="stock-compartido-permission-banner"
                >
                    <p class="tw:font-semibold">
                        No tienes permiso sobre este evento.
                    </p>
                </div>

                <div class="tw:rounded tw:border tw:border-gray-200 tw:bg-gray-50 tw:p-4">
                    <div class="tw:flex tw:items-center tw:gap-3">
                        <ToggleSwitch
                            id="stock-compartido-toggle"
                            data-cy="stock-compartido-toggle"
                            :value="stockCompartido"
                            :disabled="!enabled || loading || venueBackedDisabled || permissionDenied"
                            aria-label="Activar stock compartido"
                            @input="onToggleInput"
                        >
                            Stock compartido
                        </ToggleSwitch>
                    </div>

                    <div class="tw:mt-4 tw:max-w-xs">
                        <BaseInputNumber
                            id="stock-compartido-cupo"
                            ref="cupoInputRef"
                            :value="cupoTotal"
                            :min="0"
                            :step="1"
                            :disabled="!enabled || loading || venueBackedDisabled || permissionDenied || !stockCompartido"
                            :readonly="!stockCompartido"
                            :error="cupoTotalError || undefined"
                            :rules="stockCompartido ? 'required|numeric|min_value:0' : ''"
                            label="Cupo total"
                            :aria-invalid="cupoTotalError ? 'true' : 'false'"
                            :aria-describedby="stockCompartido ? 'stock-compartido-hint' : 'stock-compartido-hint'"
                            data-cy="stock-compartido-cupo"
                            @input="onCupoInput"
                        />
                        <small
                            id="stock-compartido-hint"
                            class="tw:block tw:text-gray-500 tw:text-xs tw:mt-1"
                            data-cy="stock-compartido-hint"
                        >
                            <template v-if="!stockCompartido">
                                Activa stock compartido para editar el cupo
                            </template>
                            <template v-else>
                                Número máximo de entradas que comparten stock.
                            </template>
                        </small>
                        <p
                            v-if="cupoTotalError"
                            id="stock-compartido-cupo-error"
                            class="tw:text-red-600 tw:text-xs tw:mt-1"
                            role="alert"
                            data-cy="stock-compartido-cupo-error"
                        >
                            <i class="fas fa-exclamation-circle tw:mr-1" aria-hidden="true"></i>
                            {{ cupoTotalError }}
                        </p>
                    </div>
                </div>

                <div
                    v-if="canDeactivateWarning"
                    class="tw:mt-3 tw:rounded tw:border tw:border-amber-300 tw:bg-amber-50 tw:p-3"
                    role="status"
                    data-cy="stock-compartido-deactivation-warning"
                >
                    <p class="tw:text-amber-700 tw:text-sm">
                        <i class="fas fa-exclamation-triangle tw:mr-2" aria-hidden="true"></i>
                        {{ entradasActivas }} entradas activas dejarán de consumir
                        stock compartido al desactivar.
                    </p>
                </div>

                <div class="tw:mt-4 tw:flex tw:items-center tw:gap-3">
                    <Button
                        id="stock-compartido-save"
                        data-cy="stock-compartido-save"
                        :disabled="!enabled || loading || venueBackedDisabled || permissionDenied"
                        :loading="loading"
                        @click="onSave"
                    >
                        Guardar
                    </Button>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
    import { ref, onMounted, nextTick, getCurrentInstance } from 'vue';

    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue';
    import BaseInputNumber from '@/modules/admin/ui/BaseInputNumber.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';

    import { useStockCompartido } from '@/composables/admin/events/sesiones/useStockCompartido';

    const props = defineProps({
        eventoId: {
            type: [Number, String],
            required: true,
        },
        sesionId: {
            type: [Number, String],
            required: true,
        },
        initialData: {
            type: Object,
            default: () => ({}),
        },
        enabled: {
            type: Boolean,
            default: true,
        },
    });

    const instance = getCurrentInstance();
    const toast = instance ? instance.proxy.$toast : null;
    const config = instance ? instance.proxy.config : null;

    const cupoInputRef = ref(null);

    const {
        stockCompartido,
        cupoTotal,
        entradasActivas,
        venueBackedDisabled,
        cupoTotalError,
        dataUnavailable,
        permissionDenied,
        loading,
        canDeactivateWarning,
        syncFromResponse,
        load,
        save,
    } = useStockCompartido({
        eventoId: props.eventoId,
        sesionId: props.sesionId,
        config,
        toast,
    });

    // If the parent already has the session payload, hydrate immediately to avoid
    // an extra round-trip. The composable still re-validates against the GET.
    if (props.initialData && Object.keys(props.initialData).length > 0) {
        syncFromResponse(props.initialData);
    }

    function focusCupoInput() {
        nextTick(() => {
            const wrapper = cupoInputRef.value;
            const root = wrapper && wrapper.$el ? wrapper.$el : null;
            const input = root && root.querySelector
                ? root.querySelector('input')
                : document.getElementById('stock-compartido-cupo');
            if (input && typeof input.focus === 'function') {
                input.focus();
            }
        });
    }

    function onToggleInput(value) {
        stockCompartido.value = Boolean(value);
        cupoTotalError.value = null;

        // REQ-SC-03 / REQ-SC-16: when flipping OFF→ON, focus moves to the cupo input.
        if (value === true) {
            focusCupoInput();
        }
    }

    function onCupoInput(value) {
        cupoTotal.value = value;
        if (cupoTotalError.value) {
            cupoTotalError.value = null;
        }
    }

    async function onSave() {
        await save();
    }

    onMounted(() => {
        load();
    });
</script>